import { memo, type ReactElement, useCallback } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Slider } from '@hivebrite/design-system/Slider';

import type { FrameChild } from 'hb-builder/components/BuilderComponents/page/block/Strips/types/items';
import { stripImageRecommendedSize } from 'hb-builder/components/BuilderComponents/shared/common/imageSizes';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import { Item } from 'hb-builder/components/common/Item';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { ChildrenRenderer } from 'hb-builder/components/Renderer/ChildrenRenderer';
import {
  useBuilderContext,
  useComponentContext,
  usePanelContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { StripsItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/stripsItemAttributes';

import { perPageBreakpoints } from './utils/constants';

const StripsSlider: BuilderComponent = () => <div />; // never used

interface StripsSliderUIProps {
  renderItem: (item: FrameChild) => ReactElement;
  stripSlider: DocumentItem;
}

const StripsSliderUI = ({ renderItem, stripSlider }: StripsSliderUIProps) => {
  const stripSliderItems = stripSlider.children as FrameChild[];

  // TODO: migration, add attribute `numberOfItems` for all templates
  const numberOfItemsStr =
    stripSlider.template === 'StripsTemplateWithPlainImages'
      ? '3'
      : stripSlider.attributes?.numberOfItems || '4';

  const numberOfItems = parseInt(numberOfItemsStr, 10);

  if (!stripSliderItems) {
    return null;
  }

  return (
    <SliderWrapper dataTestId="strips-slider">
      <Slider
        items={stripSliderItems}
        perPageBreakpoints={perPageBreakpoints({
          numberOfItems,
        })}
        loading={false}
        renderItemComponent={renderItem}
        renderLoadingItemComponent={() => <div />}
        total={stripSliderItems.length}
      />
    </SliderWrapper>
  );
};

const RenderBuilder = () => {
  const { updateAttribute } = useBuilderContext();
  const { item } = useComponentContext();
  const { displayPanel } = usePanelContext();

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    displayPanel();
    toggleDrawer(item);
  };

  const toggleDrawer = (stripsItem) => {
    const isOpened = item.attributes?.__isSelected === stripsItem.id;

    if (isOpened) {
      return;
    }

    updateAttribute({
      id: item.id,
      attribute: '__isSelected',
      value: stripsItem.id,
    });
  };

  const handleOnClick = () => toggleDrawer(item);

  const renderItem = useCallback(
    (item) => {
      const [image] = item.children;

      return (
        <Box
          flexDirection="column"
          height="100%"
          width="100%"
          onClick={handleOnClick}
          onDoubleClick={handleDoubleClick}
        >
          {/* @ts-expect-error not typed */}
          <Item item={item} />
          <ImageSetter
            attributeName="url"
            item={image}
            imageSize={stripImageRecommendedSize[item.template]}
          />
        </Box>
      );
    },
    [handleDoubleClick, handleOnClick],
  );

  return <StripsSliderUI renderItem={renderItem} stripSlider={item} />;
};

interface ChildMemoProps {
  childItem: FrameChild;
}

const ChildMemo = memo(
  ({ childItem }: ChildMemoProps) => (
    <Box flexDirection="column" height="100%" width="100%">
      <ChildrenRenderer item={{ children: [childItem] }} />
    </Box>
  ),
  () => true,
);

const Render = ({
  item = { id: '', type: '' },
}: RenderProps<DocumentItem<StripsItemAttributes>>) => (
  <StripsSliderUI
    renderItem={(childItem) => <ChildMemo childItem={childItem} />}
    stripSlider={item}
  />
);

StripsSlider.componentName = 'StripsSlider';
StripsSlider.componentType = 'single';

StripsSlider.canBeDoubleClicked = false;
StripsSlider.canBeDragged = false;
StripsSlider.canBeListed = false;
StripsSlider.canBeSelected = false;

StripsSlider.RenderBuilder = RenderBuilder;
StripsSlider.Render = Render;

StripsSlider.item = ({ attributes = {}, children, template }) => ({
  attributes,
  children,
  template,
  type: StripsSlider.componentName,
});

export default StripsSlider;

const SliderWrapper = styled(Box).attrs(() => ({
  my: 6,
  width: '100%',
}))`
  .slider-container {
    width: 100%;
  }
`;
